import type { Area } from 'react-easy-crop';

/** Proporção do cartão (legado Cropper.js). */
export const BANNER_ASPECT = 850 / 1272;
export const BANNER_CROP_WIDTH = 850;
export const BANNER_CROP_HEIGHT = 1272;

/** Banners na S3 passam pelo BFF; URLs públicas (fixecrm.com) usam direto. */
const BANNER_S3_PROXY_PATTERN = /amazonaws\.com\/banners\//i;

/**
 * Corrige URL legada corrompida pelo post-hook do Client
 * (`s3Base + "/" + "https://www.fixecrm.com/..."`).
 */
export function normalizeBannerUrl(bannerUrl: string): string {
  if (!bannerUrl) return bannerUrl;

  const embeddedAbsolute = bannerUrl.match(/amazonaws\.com\/(https?:\/\/.+)$/i);
  if (embeddedAbsolute?.[1]) return embeddedAbsolute[1];

  const secondHttps = bannerUrl.indexOf('https://', 1);
  if (bannerUrl.startsWith('http') && secondHttps > 0) {
    return bannerUrl.slice(secondHttps);
  }

  return bannerUrl;
}

/** URL para exibir imagem de banner (proxy só quando necessário). */
export function resolveBannerSrc(bannerUrl: string): string {
  const url = normalizeBannerUrl(bannerUrl);
  if (!url) return url;
  // Só faz proxy de objetos reais em /banners/ na S3 (não URLs já absolutas externas)
  if (BANNER_S3_PROXY_PATTERN.test(url)) {
    return `/bff/assets/banner-image?url=${encodeURIComponent(url)}`;
  }
  return url;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.crossOrigin = 'anonymous';
    img.src = src;
  });
}

/** Gera JPEG 850×1272 a partir da área recortada (paridade com o dash legado). */
export async function getCroppedBannerBlob(
  imageSrc: string,
  pixelCrop: Area,
  fileName = 'banner.jpg',
): Promise<File> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = BANNER_CROP_WIDTH;
  canvas.height = BANNER_CROP_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas não disponível');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    BANNER_CROP_WIDTH,
    BANNER_CROP_HEIGHT,
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Falha ao gerar imagem'))),
      'image/jpeg',
      0.92,
    );
  });

  return new File([blob], fileName, { type: 'image/jpeg' });
}

/** Evita shift de timezone na validação yup `date().min(hoje)` da API. */
export { toServerDate as toApiDateTime, fromServerDate as toFormDate } from '@/shared/lib/server-date';
